import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, Gauge, Calendar as CalIcon, Fuel, Cog, Palette, Car as CarIcon, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import TestDriveDialog from "@/components/TestDriveDialog";
import FinancingDialog from "@/components/FinancingDialog";
import ShareButtons from "@/components/ShareButtons";
import NotifyMeDialog from "@/components/NotifyMeDialog";
import { BRAND } from "@/lib/brand";
import { useSeo, useJsonLd } from "@/lib/seo";
import { MessageSquare } from "lucide-react";
import api, { formatPrice, formatMileage } from "@/lib/api";

export default function CarDetail() {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api
      .get(`/cars/${id}`)
      .then((r) => setCar(r.data))
      .catch(() => setCar(null))
      .finally(() => setLoading(false));
  }, [id]);

  useSeo(
    car ? `${car.year} ${car.make} ${car.model} for Sale in Nampa, ID | Pit Stop Motors` : undefined,
    car ? `${car.year} ${car.make} ${car.model} — ${formatMileage(car.mileage)}, ${formatPrice(car.price)} at Pit Stop Motors, a family-owned dealership in Nampa, Idaho.` : undefined
  );
  useJsonLd(
    "car-jsonld",
    car
      ? {
          "@context": "https://schema.org",
          "@type": "Car",
          name: `${car.year} ${car.make} ${car.model}`,
          brand: { "@type": "Brand", name: car.make },
          model: car.model,
          vehicleModelDate: String(car.year),
          vehicleIdentificationNumber: car.vin || undefined,
          mileageFromOdometer: { "@type": "QuantitativeValue", value: car.mileage, unitCode: "SMI" },
          color: car.exterior_color || undefined,
          image: car.image_urls && car.image_urls[0] ? car.image_urls[0] : undefined,
          offers: {
            "@type": "Offer",
            price: car.price,
            priceCurrency: "USD",
            availability: car.status === "sold" ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
            seller: { "@type": "AutoDealer", name: "Pit Stop Motors" },
          },
        }
      : null
  );

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-20" data-testid="car-detail-loading">
        <div className="aspect-[16/9] bg-secondary animate-pulse" />
      </div>
    );
  }
  if (!car) {
    return (
      <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 text-center" data-testid="car-detail-notfound">
        <h1 className="font-serif text-4xl text-primary">Car not found</h1>
        <p className="mt-3 text-muted-foreground">It may have already found a home.</p>
        <Button asChild className="mt-6 rounded-sm">
          <Link to="/inventory">Back to inventory</Link>
        </Button>
      </div>
    );
  }

  const images = car.image_urls || [];

  const specs = [
    { label: "Year", value: car.year, Icon: CalIcon },
    { label: "Mileage", value: formatMileage(car.mileage), Icon: Gauge },
    { label: "Transmission", value: car.transmission, Icon: Cog },
    { label: "Fuel", value: car.fuel_type, Icon: Fuel },
    { label: "Drivetrain", value: car.drivetrain, Icon: CarIcon },
    { label: "Exterior", value: car.exterior_color, Icon: Palette },
    { label: "Interior", value: car.interior_color, Icon: Palette },
  ].filter((s) => s.value);

  return (
    <div data-testid="car-detail-page">
      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-8">
        <Link to="/inventory" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-inventory">
          <ArrowLeft className="h-4 w-4" /> Back to inventory
        </Link>
      </div>

      <section className="mx-auto max-w-7xl px-6 md:px-10 py-8 grid lg:grid-cols-12 gap-10">
        {/* GALLERY */}
        {images.length > 0 && (
          <div className="lg:col-span-7" data-testid="car-gallery">
            <div className="aspect-[16/10] bg-secondary overflow-hidden">
              <img
                src={images[active]}
                alt={`${car.make} ${car.model}`}
                className="h-full w-full object-cover"
              />
            </div>
            {images.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {images.map((src, i) => (
                  <button
                    key={src}
                    onClick={() => setActive(i)}
                    data-testid={`gallery-thumb-${i}`}
                    className={`aspect-square overflow-hidden border ${i === active ? "border-accent" : "border-soft"}`}
                  >
                    <img src={src} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* INFO */}
        <div className={images.length > 0 ? "lg:col-span-5" : "lg:col-span-8 lg:col-start-3"} data-testid="car-info">
          <div className="flex items-center gap-2 flex-wrap">
            {car.status === "sold" && (
              <Badge data-testid="detail-sold-badge" className="bg-accent text-accent-foreground hover:bg-accent rounded-sm uppercase tracking-[0.25em] text-xs font-bold px-4 py-1.5">
                Sold
              </Badge>
            )}
            {car.previous_price && car.previous_price > car.price && car.status !== "sold" && (
              <Badge data-testid="detail-price-reduced-badge" className="bg-accent text-accent-foreground hover:bg-accent rounded-sm uppercase tracking-wider text-[10px] gap-1">
                <TrendingDown className="h-3 w-3" /> Price Reduced
              </Badge>
            )}
            {car.status === "coming_soon" && (
              <Badge data-testid="detail-coming-soon-badge" className="bg-gold text-primary hover:bg-gold rounded-sm uppercase tracking-[0.25em] text-xs font-bold px-4 py-1.5">
                Coming Soon
              </Badge>
            )}
            {car.featured && (
              <Badge className="bg-primary text-primary-foreground hover:bg-primary rounded-sm uppercase tracking-wider text-[10px]">Featured</Badge>
            )}
            {car.status !== "sold" && car.status !== "coming_soon" && (
              <Badge variant="outline" className="rounded-sm uppercase tracking-wider text-[10px]">
                {car.status}
              </Badge>
            )}
          </div>
          <h1 className="mt-3 font-serif text-4xl md:text-5xl text-primary leading-tight tracking-tight">
            {car.year} {car.make} {car.model}
          </h1>
          <p className="mt-3 flex items-baseline gap-3">
            <span className={`text-3xl font-serif ${car.status === "sold" ? "text-muted-foreground line-through" : "text-primary"}`}>{formatPrice(car.price)}</span>
            {car.previous_price && car.previous_price > car.price && car.status !== "sold" && (
              <span className="text-lg text-muted-foreground line-through" data-testid="detail-old-price">{formatPrice(car.previous_price)}</span>
            )}
          </p>
          {car.status === "sold" && (
            <p className="mt-2 text-sm text-muted-foreground" data-testid="detail-sold-note">
              This one found a home — but we get new vehicles every week. Give us a call!
            </p>
          )}

          <Separator className="my-6" />

          <p className="text-muted-foreground leading-relaxed whitespace-pre-line" data-testid="car-description">{car.description}</p>

          <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4">
            {specs.map((s) => (
              <div key={s.label} className="flex items-start gap-3" data-testid={`spec-${s.label.toLowerCase()}`}>
                <s.Icon className="h-4 w-4 mt-0.5 text-accent" strokeWidth={1.5} />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</p>
                  <p className="text-sm text-primary font-medium">{s.value}</p>
                </div>
              </div>
            ))}
          </div>

          {car.features && car.features.length > 0 && (
            <div className="mt-8">
              <p className="overline">Features</p>
              <ul className="mt-3 grid grid-cols-2 gap-2">
                {car.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-primary">
                    <Check className="h-3.5 w-3.5 text-accent" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            {car.status === "coming_soon" ? (
              <NotifyMeDialog
                car={car}
                trigger={
                  <Button size="lg" className="bg-gold text-primary hover:bg-gold/90 rounded-sm flex-1 font-semibold" data-testid="open-notify-me">
                    Notify Me When Available
                  </Button>
                }
              />
            ) : (
              <TestDriveDialog
                car={car}
                trigger={
                  <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm flex-1" data-testid="open-test-drive">
                    Schedule Test Drive
                  </Button>
                }
              />
            )}
            <FinancingDialog
              car={car}
              trigger={
                <Button size="lg" variant="outline" className="rounded-sm flex-1" data-testid="open-financing">
                  Ask About Financing
                </Button>
              }
            />
          </div>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="mt-3 w-full rounded-sm gap-2 border-accent text-accent hover:bg-accent hover:text-accent-foreground"
            data-testid="text-us-btn"
          >
            <a href={`sms:${BRAND.phoneHref.replace("tel:", "")}?body=${encodeURIComponent(`Hi Pit Stop Motors! I'm interested in the ${car.year} ${car.make} ${car.model} — ${window.location.href}`)}`}>
              <MessageSquare className="h-4 w-4" /> Text Us About This Car
            </a>
          </Button>

          <div className="mt-6 pt-6 border-t border-soft">
            <ShareButtons car={car} />
          </div>
        </div>
      </section>

      {/* VIDEO WALKAROUND */}
      {car.video_url && (
        <section className="mx-auto max-w-7xl px-6 md:px-10 pb-16" data-testid="car-video-section">
          <p className="overline">Walkaround</p>
          <h2 className="mt-2 font-serif text-3xl text-primary">See it up close</h2>
          <div className="mt-5 max-w-3xl aspect-video bg-black overflow-hidden">
            {(() => {
              const ytId = (car.video_url.match(/(?:youtu\.be\/|v=|shorts\/|embed\/)([\w-]{11})/) || [])[1];
              if (ytId) {
                return (
                  <iframe
                    src={`https://www.youtube.com/embed/${ytId}`}
                    title="Video walkaround"
                    className="h-full w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="car-video-youtube"
                  />
                );
              }
              if (/youtu\.?be/.test(car.video_url)) {
                return (
                  <div className="h-full w-full flex items-center justify-center">
                    <a href={car.video_url} target="_blank" rel="noreferrer" className="text-accent-bright underline" data-testid="car-video-link">
                      Watch the video walkaround
                    </a>
                  </div>
                );
              }
              return <video src={car.video_url} controls playsInline className="h-full w-full" data-testid="car-video-file" />;
            })()}
          </div>
        </section>
      )}
    </div>
  );
}
